import { CONSTELLATION_SIZE } from '../constants';
import type { ConstellationDot } from '../types';
import {
	getCycleProgress,
	getGlowSparkStrength,
	getSineWaveStrength,
	getWrappedCycleDistance,
} from './poseHelpers';
import { getRingCoordinates, getRingPoint, OUTER_RING_RADIUS } from './ring';

type SpeakingDotInput = Readonly<{
	audioGain: number;
	color: string;
	index: number;
	timeSeconds: number;
}>;

const ANIMATION_DURATION = 2.4;
const SPEAKING_BEATS = [
	{ center: 0.12, width: 0.075 },
	{ center: 0.42, width: 0.09 },
	{ center: 0.74, width: 0.11 },
] as const;
const CENTER_DOT_RADIAL_RESPONSE = 0.3;
const RADIAL_PULSE_BASELINE = 0.28;
const RADIAL_TRAVEL_RATIO = 0.055;
const ROTATION_RADIANS_PER_SECOND = 0.21;

const GLOW_WAVE_RADIANS_PER_SECOND = 2.9;
const GLOW_PHASE_OFFSET_RADIANS = 1.7;
// Controls how tightly the glow follows the traveling highlight around the formation.
const GLOW_CONCENTRATION_POWER = 2;
const MINIMUM_GLOW_ENVELOPE = 0.35;
const SPEAKING_GLOW_ENVELOPE = 0.65;

const getSpeakingCadence = (timeSeconds: number): number => {
	const cycle = getCycleProgress(timeSeconds, ANIMATION_DURATION);

	// Gaussian pulses: creates three smooth, uneven speaking beats during each cycle.
	return Math.max(
		...SPEAKING_BEATS.map(({ center, width }) =>
			Math.exp(-0.5 * (getWrappedCycleDistance(cycle, center) / width) ** 2),
		),
	);
};

export const createSpeakingDot = ({
	audioGain,
	color,
	index,
	timeSeconds,
}: SpeakingDotInput): ConstellationDot => {
	const { count, radius, ringIndex } = getRingCoordinates(index);
	const isCenterDot = index === 0;
	const pulse = getSpeakingCadence(timeSeconds);
	const radialResponse = isCenterDot ? CENTER_DOT_RADIAL_RESPONSE : radius / OUTER_RING_RADIUS;

	const glowWave = getSineWaveStrength(
		timeSeconds * GLOW_WAVE_RADIANS_PER_SECOND + index * GLOW_PHASE_OFFSET_RADIANS,
	);

	// Ensures that the formation pulses together
	const glowStrength =
		getGlowSparkStrength(glowWave, GLOW_CONCENTRATION_POWER) *
		(MINIMUM_GLOW_ENVELOPE + pulse * SPEAKING_GLOW_ENVELOPE);

	const radialBurst =
		(pulse - RADIAL_PULSE_BASELINE) *
		CONSTELLATION_SIZE *
		RADIAL_TRAVEL_RATIO *
		audioGain *
		radialResponse;

	return {
		...getRingPoint(
			ringIndex,
			count,
			radius + radialBurst,
			timeSeconds * ROTATION_RADIANS_PER_SECOND,
		),
		brightness: isCenterDot ? 1 : 0.66 + pulse * 0.34 * audioGain,
		color,
		glowStrength,
		scale: isCenterDot ? 1.02 + pulse * 0.34 * audioGain : 0.94 + pulse * 0.2 * audioGain,
	};
};
